import React, {useEffect, useMemo, useState} from 'react';
import {
	DropdownCompat as Dropdown,
	IDropdownOption,
	SeparatorCompat as Separator,
	StackCompat as Stack,
} from 'components/FluentUICompat';
import {AppendButton} from './AppendButton';
import {useFieldArray, useForm, useWatch} from 'react-hook-form';
import {
	useGetReferencePickerRegulationsQuery,
	useGetRegulatoryReferencePickerRegulatoryDocumentsLazyQuery,
} from './hooks/getReferencePickerData.generated';
import {ControlledTagPicker} from './ControlledTagPicker';
import {useTranslation} from 'react-i18next';
import {ReferencesTooltipTranslationProvider} from 'features/localizedTooltips';
import {useGetAllReferencesQuery} from 'features/AdminSection/hooks/references.generated';
import {ReferenceType} from 'types';
import {LoadWrapper} from 'components/LoadWrapper';
import {DocumentReferenceType} from 'features/RegulatoryDocuments/regulatoryUtils';
import {ControlledToggle} from './ControlledToggle/ControlledToggle';
import {
	RegulatoryReferencePickerProps,
	RegulatoryReferencePickerSectionState,
	RegulatoryReferencePickerState,
} from './RegulatoryReferencePicker/RegulatoryReferencePickerTypes';
import {RegulatoryReferencePickerDeleteButton} from './RegulatoryReferencePicker/RegulatoryReferencePickerDeleteButton';

export const RegulatoryReferencePicker: React.FC<
	RegulatoryReferencePickerProps
> = ({label, onReferenceSelected, inputReferences}) => {
	const {data: regulationsData, loading: regulationsLoading} =
		useGetReferencePickerRegulationsQuery();
	const [getRegDocsLazy, {loading: regDocsLoading}] =
		useGetRegulatoryReferencePickerRegulatoryDocumentsLazyQuery();

	const {t} = useTranslation('components/referencepicker');
	const {data: referenceData, loading: referencesLoading} =
		useGetAllReferencesQuery();
	const {
		control: internalControl,
		setValue: setInternalValue,
		getValues: getInternalValues,
	} = useForm();

	const [state, setState] = useState<RegulatoryReferencePickerState>({
		selectableRegulations: [],
		allRegulations: [],
		sections: [],
	});

	useEffect(() => {
		const regulations = regulationsData?.regulations ?? [];
		if (regulations.length > 0) {
			const allRegulations = regulations.map(r => {
				return {id: r.id, name: r.regulationNumber};
			});
			setState(prevState => {
				return {
					...prevState,
					selectableRegulations: [...allRegulations],
					allRegulations: [...allRegulations],
				};
			});
		}
	}, [regulationsData]);

	const {fields, remove, append} = useFieldArray({
		control: internalControl,
		name: 'regulatories',
	});

	useEffect(() => {
		const documentReferences: DocumentReferenceType[] = [];
		state.sections.forEach(section => {
			const regulation = section.regulation || null;
			const selectedDocuments = section.selectAll
				? section.selectableRegulatoryDocuments.map(srd => srd.id)
				: section.selectedRegulatoryDocuments;
			documentReferences.push({
				reference: {id: section.reference || ''},
				referenceDocumentType: 'REGULATORY_DOCUMENT',

				documentReferenceGroup: {
					regulationReference: regulation ? {id: regulation} : null,
					regulatoryDocumentReferences: selectedDocuments.map(id => ({id})),
					selectAllRegulatoryDocuments: section.selectAll,
				},
				selectAllRegulatoryDocuments: section.selectAll,
			});
		});
		onReferenceSelected(documentReferences);
	}, [state]);

	const regulations = useMemo(
		() =>
			regulationsData?.regulations?.map(r => {
				return {
					id: r.id,
					name: r.regulationNumber,
					key: r.id,
				};
			}) ?? [],
		[regulationsData],
	);

	const references = useMemo(
		() =>
			referenceData?.references
				?.filter(ref =>
					ref.referenceTypes.some(type =>
						[
							ReferenceType.Regulation,
							ReferenceType.RegulatoryDocument,
						].includes(type),
					),
				)
				.map(r => {
					return {key: r.id, text: r.name};
				}) ?? [],
		[referenceData],
	);

	const fillInternalValues = (
		newSections: RegulatoryReferencePickerSectionState[],
	) => {
		newSections.forEach((ns, index) => {
			append({});
			if (ns.regulation) {
				const name = regulations.find(r => r.id === ns.regulation)?.name;
				if (name) {
					setInternalValue(`regulatories[${index}].regulation`, [
						{
							id: ns.regulation,
							key: ns.regulation,
							name,
						},
					]);
				}
			}
			const selectedDocuments = ns.selectableRegulatoryDocuments.filter(
				srd => ns.selectedRegulatoryDocuments.indexOf(srd.id) > -1,
			);

			if (selectedDocuments.length > 0) {
				setInternalValue(
					`regulatories[${index}].regulatoryDocuments`,
					selectedDocuments.map(sd => {
						return {
							id: sd.id,
							key: sd.id,
							name: sd.name,
						};
					}),
				);
			}
		});
	};

	useEffect(() => {
		const initValues = async () => {
			if (inputReferences.length === 0 && regulations.length === 0) {
				console.debug(
					`>>> input references or regulations length is 0. Skipping RegulatoryReferencePicker initialization...`,
				);
				return;
			}

			const newSections: RegulatoryReferencePickerSectionState[] = [];

			/* eslint-disable no-await-in-loop */
			for (const ref of inputReferences) {
				const group = ref.documentReferenceGroup;
				const regulationId = group?.regulationReference?.id;
				const referenceId = ref.reference?.id;
				const selectAll = group?.selectAllRegulatoryDocuments ?? false;
				const regDocIds =
					group?.regulatoryDocumentReferences?.map(r => r?.id) ?? [];

				if (!regulationId) {
					continue;
				}

				const myRegulation = regulations.find(r => r.id === regulationId);
				if (!myRegulation) {
					continue;
				}

				const documents = await getRegDocsLazy({
					variables: {regulationId},
				});

				const selectableRegulatoryDocuments =
					documents.data?.regulation?.regulatoryDocuments?.map(rd => ({
						id: rd.id,
						name: rd.name,
						key: rd.id,
					})) ?? [];

				const selectedRegulatoryDocuments = selectAll
					? selectableRegulatoryDocuments.map(srd => srd.id)
					: selectableRegulatoryDocuments
							.filter(srd => regDocIds.includes(srd.id))
							.map(srd => srd.id);

				newSections.push({
					selectableRegulatoryDocuments,
					selectedRegulatoryDocuments,
					regulation: myRegulation.id,
					selectAll,
					documentsLoaded: true,
					reference: referenceId,
				});
			}
			setState({
				...state,
				sections: newSections,
			});

			fillInternalValues(newSections);
		};
		initValues();
	}, [inputReferences, regulations]);

	const selectAllToggle = (index: number) => {
		return (
			<ControlledToggle
				control={internalControl}
				name={`regulatories[${index}].selectAll`}
				label={
					<Stack>
						<Stack.Item>
							<b>{t('SelectAll')}</b>
						</Stack.Item>
						<Stack.Item>{t('SelectAllDetails')}</Stack.Item>
					</Stack>
				}
				defaultChecked={state.sections[index].selectAll}
				onChange={(_: any, val: any) => {
					const newVal = Boolean(val);
					const newSection = {
						...state.sections[index],
						selectAll: newVal,
					};
					const newSections = [...state.sections];
					newSections[index] = newSection;
					setState({
						...state,
						sections: newSections,
					});
					return newVal;
				}}
			/>
		);
	};

	const onRegulationSelected = async (items: {id: string}[], index: number) => {
		if (items?.length === 0) {
			const sections = [...state.sections];
			sections[index].selectedRegulatoryDocuments = [];
			sections[index].selectableRegulatoryDocuments = [];
			sections[index].regulation = undefined;
			setState({
				...state,
				sections,
			});
			setInternalValue(`regulatories[${index}].regulatoryDocuments`, undefined);
		} else {
			const regulationId = items[0].id;
			const documents = await getRegDocsLazy({variables: {regulationId}});
			const newSection: RegulatoryReferencePickerSectionState = {
				regulation: regulationId,
				selectableRegulatoryDocuments:
					documents?.data?.regulation?.regulatoryDocuments?.map(rd => {
						return {id: rd.id, name: rd.name, key: rd.id};
					}) || [],
				selectedRegulatoryDocuments: [],
				selectAll: false,
				documentsLoaded: true,
				reference: state.sections[index]?.reference,
			};
			const newSections = state.sections.map((section, sectionIndex) =>
				sectionIndex === index ? newSection : section,
			);
			if (!newSections[index]) {
				newSections[index] = newSection;
			}
			setState({
				...state,
				sections: newSections,
			});
		}
	};

	const onDocumentSelected = (items: any, index: number) => {
		const mySection = state.sections[index];
		const newSection = {
			...mySection,
			selectedRegulatoryDocuments: items.map((item: any) => item.id),
		};
		const newSections = state.sections.map((section, sectionIndex) =>
			sectionIndex === index ? newSection : section,
		);
		if (!newSections[index]) {
			newSections[index] = newSection;
		}
		setState({
			...state,
			sections: newSections,
		});
		return items;
	};

	const onDropdownReferenceSelected = (
		index: number,
		event: React.FormEvent<HTMLDivElement>,
		option?: IDropdownOption,
	) => {
		let newSections: RegulatoryReferencePickerSectionState[];
		if (state.sections[index]) {
			const newSection = {
				...state.sections[index],
				reference: option?.key as string | undefined,
			};
			newSections = state.sections.map((section, sectionIndex) =>
				sectionIndex === index ? newSection : section,
			);
		} else {
			newSections = [...state.sections];
			newSections[index] = {
				reference: option?.key as string | undefined,
				documentsLoaded: false,
				selectableRegulatoryDocuments: [],
				selectAll: false,
				selectedRegulatoryDocuments: [],
				regulation: undefined,
			};
		}
		setState({
			...state,
			sections: newSections,
		});
	};

	const PickerSection: React.FC<{item: any; index: number}> = (props: {
		item: any;
		index: number;
	}) => {
		const {item, index} = props;

		return (
			<Stack.Item>
				<Stack>
					<Stack horizontal>
						{state.sections[index]?.reference !== undefined && (
							<Stack.Item styles={{root: {minWidth: 400}}}>
								<ControlledTagPicker
									control={internalControl}
									label={`${t('SelectRegulation')}`}
									name={`regulatories[${index}].regulation`}
									selectableItems={regulations}
									getKey={item => (item as any)?.id}
									getName={item => (item as any)?.name}
									rules={{required: t('RequiredField')}}
									itemLimit={1}
									performExtraActionOnChange={(items: any) =>
										onRegulationSelected(items, index)
									}
								/>
							</Stack.Item>
						)}
						{index === fields.length - 1 && (
							<Stack.Item>
								<RegulatoryReferencePickerDeleteButton
									index={index}
									remove={remove}
									setInternalValue={setInternalValue}
									setState={setState}
									state={state}
									t={t}
								/>
							</Stack.Item>
						)}
					</Stack>
					{state.sections[index]?.selectableRegulatoryDocuments !==
						undefined && (
						<>
							<Stack.Item>
								<ControlledTagPicker
									control={internalControl}
									label={`${t('SelectVersion')}`}
									name={`regulatories[${index}].regulatoryDocuments`}
									selectableItems={
										state.sections[index].selectableRegulatoryDocuments
									}
									getKey={item => (item as any)?.id}
									getName={item => (item as any)?.name}
									rules={{required: t('RequiredField')}}
									itemLimit={undefined}
									onItemSelected={(item: any) => {
										return item;
									}}
									disabled={state.sections[index].selectAll}
									performExtraActionOnChange={(items: any) =>
										onDocumentSelected(items, index)
									}
								/>
							</Stack.Item>
							<Stack.Item>{selectAllToggle(index)}</Stack.Item>
						</>
					)}
				</Stack>
			</Stack.Item>
		);
	};

	return (
		<LoadWrapper
			loading={regulationsLoading || referencesLoading}
			extraStyles={{
				root: {
					position: 'relative',
					top: '30px',
				},
			}}
		>
			<ReferencesTooltipTranslationProvider treatAsDottedFormFieldName>
				{fields.map((item: any, index: any) => (
					<>
						<Stack.Item>
							<Dropdown
								label={t('ReferenceType')}
								options={references}
								defaultSelectedKey={state.sections[index]?.reference}
								onChange={(
									event: React.FormEvent<HTMLDivElement>,
									option?: IDropdownOption,
								) => onDropdownReferenceSelected(index, event, option)}
							/>
						</Stack.Item>
						<PickerSection item={item} index={index} key={index} />
					</>
				))}
				<Stack.Item>
					<AppendButton
						onClick={() => {
							append({});
						}}
						label={label}
						name={`regulatories`}
					/>
				</Stack.Item>
			</ReferencesTooltipTranslationProvider>
		</LoadWrapper>
	);
};
